import type { EssencePool, EssenceType } from './types/essence';
import type { ItemInstance, ItemRarity } from './types/game';
import { getAllEssenceTypes } from './types/essence';
import { SeededRng } from './seeded-rng';

/** First-pass exchange rates, confirmed against a worked example — expect retuning later. */
export const ESSENCE_EXCHANGE_RATE: Readonly<Record<EssenceType, number>> = {
    resonance: 1,
    memory: 2,
    courage: 3,
    sorrow: 3,
    fury: 4,
    truth: 8,
};

export function resolveEssenceExchangeValue(pool: Partial<EssencePool>): number {
    return getAllEssenceTypes().reduce((sum, type) => sum + (pool[type] ?? 0) * ESSENCE_EXCHANGE_RATE[type], 0);
}

/** What an item with no authored essence value trades for, in resonance. */
export const DEFAULT_ITEM_ESSENCE_VALUE = 5;

/** A rarer item copy trades for more at a house of exchange. */
export const ITEM_RARITY_TRADE_MULTIPLIER: Readonly<Record<ItemRarity, number>> = {
    common: 1,
    uncommon: 1.5,
    rare: 2.25,
    epic: 3.5,
    legendary: 6,
    mythic: 10,
};
/** Each upgrade on an item copy adds this share to what it trades for. */
export const ITEM_UPGRADE_TRADE_BONUS_PER_LEVEL = 0.3;

/** How much more an item copy trades for than a plain catalog copy. */
export function resolveItemTradeMultiplier(item: { readonly instance?: ItemInstance }): number {
    const instance = item.instance;
    if (!instance) {
        return 1;
    }
    return ITEM_RARITY_TRADE_MULTIPLIER[instance.rarity] * (1 + ITEM_UPGRADE_TRADE_BONUS_PER_LEVEL * instance.upgradeLevel);
}

/**
 * The essence an item trades for at a house of exchange: its authored
 * `essenceValue` (items.essence_value), or `DEFAULT_ITEM_ESSENCE_VALUE`
 * resonance when nothing positive is authored, so no item is worthless.
 * A rare or upgraded copy is worth more (`resolveItemTradeMultiplier`).
 */
export function resolveItemEssenceValue(item: {
    readonly essenceValue: Partial<EssencePool>;
    readonly instance?: ItemInstance;
}): Partial<EssencePool> {
    const multiplier = resolveItemTradeMultiplier(item);
    const value: Partial<EssencePool> = {};
    for (const type of getAllEssenceTypes()) {
        const amount = item.essenceValue[type] ?? 0;
        if (amount > 0) {
            value[type] = Math.max(1, Math.round(amount * multiplier));
        }
    }
    return Object.keys(value).length > 0
        ? value
        : { resonance: Math.max(1, Math.round(DEFAULT_ITEM_ESSENCE_VALUE * multiplier)) };
}

/** Essence types from the least to the most valuable: what is spent first, so the rarer is kept. */
export const ESSENCE_TYPES_CHEAPEST_FIRST: readonly EssenceType[] = [...getAllEssenceTypes()]
    .sort((left, right) => ESSENCE_EXCHANGE_RATE[left] - ESSENCE_EXCHANGE_RATE[right]);

/**
 * The essence to take from `pool` to reach `value` in exchange value,
 * spending the cheapest types first so the most valuable essence is kept.
 * Whole units only, so it can overshoot `value` by less than one unit of the
 * last type taken. Takes everything if `pool` is worth less than `value`.
 */
export function resolveEssenceToTake(pool: Partial<EssencePool>, value: number): Partial<EssencePool> {
    const taken: Partial<EssencePool> = {};
    let remaining = value;
    for (const type of ESSENCE_TYPES_CHEAPEST_FIRST) {
        const available = pool[type] ?? 0;
        if (remaining <= 0 || available <= 0) {
            continue;
        }
        const amount = Math.min(available, Math.ceil(remaining / ESSENCE_EXCHANGE_RATE[type]));
        taken[type] = amount;
        remaining -= amount * ESSENCE_EXCHANGE_RATE[type];
    }
    return taken;
}

/**
 * Rolls a random essence bundle whose converted value lands within ±20% of
 * targetValue, weighted toward favoredTypes. Untuned first pass — the
 * composition heuristics (1 vs 2 types, 5x favored-type weighting) are
 * explicit placeholders, not tuned game balance.
 */
export function rollEssenceRewardBundle(
    targetValue: number,
    favoredTypes: readonly EssenceType[],
    rng: SeededRng,
): Partial<EssencePool> {
    if (targetValue <= 0) {
        return {};
    }
    const rolledValue = Math.max(1, Math.round(targetValue * (0.8 + rng.random() * 0.4)));

    const weighted: EssenceType[] = [];
    for (const type of getAllEssenceTypes()) {
        const weight = favoredTypes.includes(type) ? 5 : 1;
        for (let i = 0; i < weight; i += 1) {
            weighted.push(type);
        }
    }
    const shuffled = rng.shuffle(weighted);
    const typeCount = rng.random() < 0.7 ? 1 : 2;
    const chosen: EssenceType[] = [];
    for (const type of shuffled) {
        if (!chosen.includes(type)) {
            chosen.push(type);
        }
        if (chosen.length >= typeCount) {
            break;
        }
    }

    const ratio = chosen.length === 2 ? 0.3 + rng.random() * 0.4 : 1;
    const bundle: Partial<EssencePool> = {};
    chosen.forEach((type, index) => {
        const valueUnits = index === 0 ? rolledValue * ratio : rolledValue * (1 - ratio);
        bundle[type] = Math.max(1, Math.round(valueUnits / ESSENCE_EXCHANGE_RATE[type]));
    });
    return bundle;
}

/**
 * Essence paid out inside one Crucible: exactly `targetValue` in its
 * `essenceDropType` when it pins one (a commission's purpose-built
 * Crucible), otherwise a `rollEssenceRewardBundle` weighted toward
 * `favoredTypes`.
 */
export function rollCrucibleEssenceBundle(
    targetValue: number,
    favoredTypes: readonly EssenceType[],
    essenceDropType: EssenceType | undefined,
    rng: SeededRng,
): Partial<EssencePool> {
    if (essenceDropType) {
        return { [essenceDropType]: Math.max(1, Math.round(targetValue / ESSENCE_EXCHANGE_RATE[essenceDropType])) };
    }
    return rollEssenceRewardBundle(targetValue, favoredTypes, rng);
}
